import { useEffect, useState } from "react";
import { useAppSelector } from "../../../../store";
import { getAbo, getAff, storedLocale } from "../../../Authentication/util";
import {
  useGetMonthlyIncomeBreakdownQuery,
  useGetMonthlyPerformanceIncomeQuery,
} from "../../../../services/MonthlyPerformanceIncome/monthlyPerformanceIncomeAPI";
import type {
  MonthlyPerformanceIncomeData,
  MonthlyIncomeBreakdownResponse,
} from "../../../../services/MonthlyPerformanceIncome/monthlyPerformanceIncomeAPI.types";
import { lambdaErrorCheck } from "../../../../common/util/errorHandling";

export const defaultMonthlyIncomeData = (): MonthlyPerformanceIncomeData => {
  return {
    GroupPv: 0,
    CurrentPerformanceBonusLevel: 0,
    CurrentPerformanceBonusVolume: 0,
    NextPerformanceBonusLevel: 0,
    NextPerformanceBonusVolume: 0,
    VolumeNeeded: 0,
    CorePlanQualified: false,
    QualifiedLegs: 0,
    Schedules: {
      ISOCountry: "",
      ISOCurrency: "",
      bonusDetails: [
        {
          Volume: 0,
          Percent: 0,
        },
      ],
    },
    CallResults: [],
    Error: null,
    PerformanceBonus: {
      gpvNeeded: {
        Actual: 0,
        Formatted: "",
      },
      isSpQualified: false,
      headerVariant: "",
      percentNeeded: {
        Actual: 0,
        Formatted: "",
      },
      perfBonusScaleData: {
        max: 0,
        progressValue: 0,
        ticks: [],
        milestones: [],
        legends: [],
      },
      performanceBonusPercent: {
        Actual: 0,
        Formatted: "",
      },
      qualifiedLegs: "",
      totalGpv: {
        Actual: 0,
        Formatted: "",
      },
      schedules: [
        {
          volume: {
            Actual: 0,
            Formatted: "",
          },
          percent: {
            Actual: 0,
            Formatted: "",
          },
        },
      ],
    },
  };
};

export const defaultMonthlyIncomeBreakdownData = (): MonthlyIncomeBreakdownResponse => {
  return {
    CorePlanBonuses: [],
    CorePlusIncentives: [],
    NavigationItems: [],
    TotalIncome: "$0.00",
    CallResults: [],
    Error: null,
  };
};

export const FetchMonthlyPerformanceIncomeData = (period: string) => {
  const { user, configuration } = useAppSelector((state) => state.boot);
  const {
    data: payloadData,
    isFetching,
    isError,
    refetch,
  } = useGetMonthlyPerformanceIncomeQuery({
    aff: `${getAff()}`,
    abo: `${getAbo()}`,
    isoCountryCode: `${user.isoCountryCode}`,
    period,
    locale: storedLocale(),
  });

  const [isLoading, setIsLoading] = useState(true);
  const [errorStatus, setErrorStatus] = useState(false);
  const [monthlyPerformanceIncomeData, setMonthlyPerformanceIncomeData] =
    useState<MonthlyPerformanceIncomeData>(defaultMonthlyIncomeData());

  useEffect(() => {
    setIsLoading(isFetching);
    setErrorStatus(isError);
    if (payloadData && !isFetching && !isError) {
      setMonthlyPerformanceIncomeData(payloadData);
      const foundLambdaError = lambdaErrorCheck(payloadData.Error);
      setErrorStatus(foundLambdaError);
    }
  }, [isFetching, period, isError, payloadData]);

  return { isLoading, monthlyPerformanceIncomeData, errorStatus, refetch };
};

export const FetchMonthlyIncomeBreakdownData = (period: string) => {
  const { user, configuration } = useAppSelector((state) => state.boot);
  const {
    data: payloadData,
    isFetching,
    isError,
    refetch,
  } = useGetMonthlyIncomeBreakdownQuery({
    aff: `${getAff()}`,
    abo: `${getAbo()}`,
    locale: storedLocale(),
    isoCountryCode: `${user.isoCountryCode}`,
    period,
  });

  const [isIncomeBreakdownLoading, setIsIncomeBreakdownLoading] = useState(true);
  const [incomeBreakdownErrorStatus, setIncomeBreakdownErrorStatus] = useState(false);
  const [monthlyIncomeBreakdownData, setMonthlyIncomeBreakdownData] = useState<MonthlyIncomeBreakdownResponse>(
    defaultMonthlyIncomeBreakdownData(),
  );

  useEffect(() => {
    setIsIncomeBreakdownLoading(isFetching);
    setIncomeBreakdownErrorStatus(isError);
    if (payloadData && !isFetching && !isError) {
      setMonthlyIncomeBreakdownData(payloadData);
      const foundLambdaError = lambdaErrorCheck(payloadData.Error);
      setIncomeBreakdownErrorStatus(foundLambdaError);
    }
  }, [isFetching, period, isError, payloadData]);

  return { isIncomeBreakdownLoading, monthlyIncomeBreakdownData, incomeBreakdownErrorStatus, refetch };
};
